import { ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import _ from 'lodash'
import { service } from 'boot/service'
import useNotify from 'src/hook/useNotify'
import { useAuthStore, useTemplateStore } from 'src/stores'
import { errorArrayGenerator } from 'src/common/helpers/generator/error.helper'
import { generateImagePath } from 'src/common/helpers/generator/filePath.helper'
import translate from 'src/common/helpers/language/translate'
import translationObject from '../../translate/baseTemplate.translate'
import globalTranslateObject from 'src/translate/global.translate'
import { TemplateBackgroundTypeEnum } from 'src/common/enums'

export default function useBaseTemplateHeaderHook() {
  const route = useRoute()
  const router = useRouter()
  const { notify } = useNotify()
  const { t } = translate(translationObject)
  const { t: globalT } = translate(globalTranslateObject)
  const authStore = useAuthStore()
  const templateStore = useTemplateStore()
  const isLoading = ref(false)

  const shapes = [
    { label: t('contents.shapes.rectangle'), value: { value: 'rectangle' } },
    { label: t('contents.shapes.circle'), value: { value: 'circle' } },
    {
      label: t('contents.shapes.line'),
      value: { value: 'line', width: 200, height: 2 },
    },
    { label: t('contents.shapes.star'), value: { value: 'star' } },
  ]

  async function handleSaveTemplateChanges() {
    try {
      const isImageBackground =
        templateStore.backgroundType === TemplateBackgroundTypeEnum.IMAGE && templateStore.background
      let sort = '-1'

      if (templateStore.uploadedImageIds.length) {
        sort = templateStore.uploadedImageIds
        if (isImageBackground) sort = [...sort, 'background']
      } else {
        if (isImageBackground) sort = 'background'
        else sort = '-1'
      }

      isLoading.value = true
      const uploadImagesPayload = {
        data: {},
        entity_id: route.params.id,
        entity_type: 'receipt',
        sort,
      }

      _.forEach(templateStore.newUploadImages, (value, key) => {
        uploadImagesPayload.data[key] = value.file
      })

      if (templateStore.backgroundType === TemplateBackgroundTypeEnum.IMAGE && templateStore.background.file)
        uploadImagesPayload.data.background = templateStore.background.file

      await Promise.all([
        uploadImagesPayload ? service.asset.upload(uploadImagesPayload) : Promise.resolve({}),
        service.settingsTemplate.upsert({
          id: route.params.id,
          items: templateStore.layers,
          name: templateStore.templateName,
          images: templateStore.uploadedImageIds,
          template_type: templateStore.templateType,
          background_type: templateStore.backgroundType,
          orientation: templateStore.orientation,
          background:
            templateStore.backgroundType === TemplateBackgroundTypeEnum.IMAGE
              ? generateImagePath(
                  { id: route.params.id, images: ['background'] },
                  'receipt',
                  authStore.portalUser.business_id,
                )
              : templateStore.background,
          colours: templateStore.colours,
        }),
      ])

      if (uploadImagesPayload) templateStore.handleResetNewImages()

      if (templateStore.isDigitalMenu) router.push({ name: 'DevicesAndProfiles', hash: '#digitalMenuTemplates' })
      else router.push({ name: 'PrintingSetups', hash: '#printTemplates' })
    } catch (error) {
      const errors = errorArrayGenerator(error)
      for (const errorMessage of errors) notify(false, errorMessage)
    } finally {
      isLoading.value = false
    }
  }

  function handleAddImageContent() {
    if (templateStore.activeLayer.index === -1) {
      if (templateStore.isDigitalMenu) templateStore.handleActiveLayer({ index: 0 })
      else return notify(false, t('add_content_toast'))
    }

    templateStore.uploadedImageIds.push(templateStore.getLastUploadedImageId + 1)
    templateStore.handleAddContent({
      type: 'image',
      overrideFields: {
        value: generateImagePath(
          { id: route.params.id, images: [templateStore.getLastUploadedImageId] },
          'receipt',
          authStore.portalUser.business_id,
        ),
      },
    })
  }

  function handleAddTextShapeContent({ type, overrideFields = {} } = {}) {
    if (templateStore.activeLayer.index === -1) {
      if (templateStore.isDigitalMenu) templateStore.handleActiveLayer({ index: 0 })
      else return notify(false, t('add_content_toast'))
    }
    templateStore.handleAddContent({ type, overrideFields })
  }

  return {
    isLoading,
    shapes,
    templateStore,
    t,
    globalT,
    handleSaveTemplateChanges,
    handleAddImageContent,
    handleAddTextShapeContent,
  }
}
